// Stripe configuration and utilities for RepConnect
import { loadStripe } from '@stripe/stripe-js';
import { logger } from './logger';

// Stripe publishable key (replace with your actual publishable key)
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here';

// Initialize Stripe
export const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Subscription plans
export const SUBSCRIPTION_PLANS = {
  sales_rep: {
    id: 'sales_rep',
    name: 'Sales Representative',
    price: 49,
    priceId: 'price_1SL8G2GThCZOasDrOcvVSswO',
    features: [
      'Unlimited meeting scheduling',
      'Provider search and contact',
      'Calendar integration',
      'Expense tracking',
      'Compliance reporting',
      'Order history and favorites'
    ]
  },
  manager: {
    id: 'manager',
    name: 'Sales Manager',
    price: 99,
    priceId: 'price_1SL8GwGThCZOasDrbOReurNk',
    features: [
      'All Sales Rep features',
      'Team dashboard and analytics',
      'Multi-rep oversight',
      'Advanced reporting',
      'Team performance metrics',
      'Bulk meeting management'
    ]
  },
  healthcare_provider: {
    id: 'healthcare_provider',
    name: 'Healthcare Provider',
    price: 0,
    priceId: null,
    features: [
      'Free forever',
      'Meeting request management',
      'Availability scheduling',
      'Dining preferences',
      'Rep approval system',
      'Calendar integration'
    ]
  }
};

// Payment and subscription utilities
export const PaymentUtils = {
  // Check if user has valid subscription
  hasValidSubscription(user) {
    if (!user) return false;

    // Healthcare providers get free access
    if (user.user_type === 'customer' || user.userType === 'customer') return true;

    // Check subscription status for sales reps and managers
    const subscription = this.getUserSubscription(user.id);

    // Return true only if valid subscription exists
    return subscription && subscription.status === 'active' && subscription.current_period_end > Date.now();
  },

  // Get user subscription from localStorage
  getUserSubscription(userId) {
    try {
      const subscription = localStorage.getItem(`subscription_${userId}`);
      return subscription ? JSON.parse(subscription) : null;
    } catch (error) {
      logger.error('Error getting user subscription:', error);
      return null;
    }
  },

  // Save user subscription to localStorage
  saveUserSubscription(userId, subscriptionData) {
    try {
      localStorage.setItem(`subscription_${userId}`, JSON.stringify(subscriptionData));
      return true;
    } catch (error) {
      logger.error('Error saving user subscription:', error);
      return false;
    }
  },

  // Update subscription status
  updateSubscriptionStatus(userId, status) {
    const subscription = this.getUserSubscription(userId);
    if (subscription) {
      subscription.status = status;
      subscription.updated_at = new Date().toISOString();
      this.saveUserSubscription(userId, subscription);
    }
  },

  // Clear user subscription from localStorage (for cancelled subscriptions)
  clearUserSubscription(userId) {
    try {
      localStorage.removeItem(`subscription_${userId}`);
      return true;
    } catch (error) {
      logger.error('Error clearing user subscription:', error);
      return false;
    }
  },

  // Get plan for user type
  getPlanForUserType(userType) {
    switch (userType) {
      case 'sales':
        return SUBSCRIPTION_PLANS.sales_rep;
      case 'manager':
        return SUBSCRIPTION_PLANS.manager;
      case 'customer':
        return SUBSCRIPTION_PLANS.healthcare_provider;
      default:
        return null;
    }
  },

  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  },

  // Check if payment is required for user type
  isPaymentRequired(userType) {
    // Customers (healthcare providers) and office staff get free access
    // Only sales reps and managers need subscriptions
    return userType === 'sales' || userType === 'manager';
  },

  // Create checkout session (simulate API call)
  async createCheckoutSession(priceId, userId, userType) {
    // In a real app, this would call your backend API
    // For demo purposes, we'll simulate a successful payment
    
    return new Promise((resolve) => {
      setTimeout(() => {
        const subscription = {
          id: `sub_${Date.now()}`,
          status: 'active',
          plan_id: priceId,
          user_id: userId,
          user_type: userType,
          current_period_start: Date.now(),
          current_period_end: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        this.saveUserSubscription(userId, subscription);
        resolve({ success: true, subscription });
      }, 2000);
    });
  },

  // Cancel subscription
  async cancelSubscription(userId) {
    // In a real app, this would call your backend API
    return new Promise((resolve) => {
      setTimeout(() => {
        this.updateSubscriptionStatus(userId, 'canceled');
        resolve({ success: true });
      }, 1000);
    });
  },

  // Get subscription status display
  getSubscriptionStatusDisplay(subscription) {
    if (!subscription) return { text: 'No subscription', color: 'text-gray-500' };
    
    switch (subscription.status) {
      case 'active':
        return { text: 'Active', color: 'text-green-600' };
      case 'past_due':
        return { text: 'Past Due', color: 'text-yellow-600' };
      case 'canceled':
        return { text: 'Canceled', color: 'text-red-600' };
      case 'incomplete':
        return { text: 'Incomplete', color: 'text-orange-600' };
      default:
        return { text: subscription.status, color: 'text-gray-500' };
    }
  },

  // Format date
  formatDate(timestamp) {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
};